/**
 * increments the value of the key by 1
 * @param obj any object with keys of type number
 * @param id any number that belongs to the keys of the object
 * @returns object with the same keys and values incremented by 1
 *
 * @example
 *   * if the key already exists, increase the value of that key by 1
 *     ```
 *     incById({a: 1, b: 2}, 'a');
 *     => {a: 2, b: 2}
 *     ```
 *
 *   *  if the key doesn't exist in the object, add the key with value 1
 *      ```
 *      incById({a: 1, b: 2}, 'c');
 *      => {a: 1, b: 2, c: 1}
 *      ```
 *
 *   *  if the key is `undefined` or `false`, return the object as is
 *      ```
 *      incById({a: 1, b: 2}, undefined);
 *      incById({a: 1, b: 2}, false);
 *      => {a: 1, b: 2}
 *      ````
 */
export const incById = <Obj extends Record<string | number, number>>(
  obj: Obj = {} as Obj,
  id?: keyof Obj | false
) =>
  id === undefined || id === false ? obj : { ...obj, [id]: (obj[id] ?? 0) + 1 };

/**
 * decrements the value of the key by 1
 * @param obj any object with keys of type number
 * @param id any number that belongs to the keys of the object
 * @returns object with the same keys and values decremented by 1
 *
 * @example
 *   * if the key already exists, decrease the value of that key by 1
 *     ```
 *     incById({a: 1, b: 2}, 'a');
 *     => {a: 0, b: 2}
 *     ```
 *
 *   *  if the key doesn't exist in the object, add the key with value 0
 *      ```
 *      incById({a: 1, b: 2}, 'c');
 *      => {a: 1, b: 2, c: 0}
 *      ```
 *
 *   *  if the key is `undefined` or `false`, return the object as is
 *      ```
 *      incById({a: 1, b: 2}, undefined);
 *      incById({a: 1, b: 2}, false);
 *      => {a: 1, b: 2}
 *      ````
 */
export const decById = <Obj extends Record<string | number, number>>(
  obj: Obj = {} as Obj,
  id?: keyof Obj | false
) =>
  id === undefined || id === false
    ? obj
    : { ...obj, [id]: Math.max((obj[id] ?? 0) - 1, 0) };

/**
 * Takes all first elements of the list, excluding the given element and the
 * rest of items in the list.
 * A couple of conditions are important:
 *  - The first element won't be removed ever.
 *  - If the element is not found, it will return the list unchanged.
 *
 * @example
 *   takeAllBefore(['a', 'b', 'c', 'd'], 'c')
 *   => ['a', 'b']
 *
 * @param {T[]} list - a list to filter from
 * @param {T} elem - an element of the list
 * @returns {T[]}
 */
export const takeAllBefore = <T>(list: T[], elem: T): T[] => {
  const stepIndex = list.indexOf(elem);
  const stepToRemove = stepIndex > -1 ? stepIndex : list.length;

  return [...list.slice(0, 1), ...list.slice(1, stepToRemove)];
};

/**
 * cleans up the list from falsy values
 * then join them into a string with a separator
 * @param list of printable values
 * @param separator
 * @returns a string joined by the separator
 */
export const join = <T extends number | string>(
  list: T[],
  separator: string
): string => list.filter(Boolean).join(separator);

/**
 * Checks if the value is NOT falsy
 * @param value
 * @returns true if the value is NOT falsy
 */
export const isSomething = <TValue>(
  value: TValue | null | undefined
): value is TValue => value !== null && value !== undefined;

/**
 * Adds the specified increment to the value only if the condition is true.
 * If the condition is false or undefined, returns the original value.
 */
export const incrementIf = (
  value: number,
  condition: boolean | undefined,
  increment = 1
): number => value + (condition ? increment : 0);
